export enum FarmAccessOption {
  Owner = 0,
  Read = 1,
  Write = 2
}

export interface BillingStatus {
  id: number;
  name: string;
}

export interface Client {
  id?: number;
  parent_client_id?: number;
  farms?: Array<Farm>;
  user_id?: number;
  name: string;
  phone_number?: string;
  billing_status?: BillingStatus;
  subscription_expiry_date: string;
  subscription?: Subscription | null;
  subscription_id: number;
  scout_app?: boolean;
  flight_app?: boolean;
  email?: string;
  role_type_id?: number;
  owner_id?: number;
}

export interface ClientFarm {
  farm_id: number;
  farm_name: string;
  client_name: string;
  client_email: string;
  client_id: number;
  access?: FarmAccessOption;
  total_hectares?: number;
  orchards?: ClientFarmOrchard[];
  shared_with_clients?: FarmSharedWithClient[];
  crop_family_id?: number;
}

export interface SharedWithClient {
  id: number;
  shared_with_client_name: string;
  shared_with_client_email: string;
  shared_with_client_id: number;
  modifying?: boolean;
}

export interface FarmSharedWithClient extends SharedWithClient {
  farm_name: string;
  farm_id: number;
  access?: FarmAccessOption;
}

export interface PolicySharedWithClient extends SharedWithClient {
  policy_name: string;
  policy_id: number;
  access?: PolicyAccessOption;
}

export interface ClientFarmOrchard {
  id: number;
  hectares: number;
  name: string;
  polygon: string;
  crop_type_id: number;
  farm_id: number;
}

export interface CropFamily {
  name: string;
  id: number;
}

export interface CropType {
  id: number;
  crop_family: CropFamily;
  symbolic_data_type: SymbolicDataType;
  name: string;
}

export interface DroneService {
  id?: number;
  client_id: number;
  service_provider_id: number | null;
  farm_id: number;
  upload_id: number | null;
  upload_complete?: boolean;
  upload_processed?: boolean;
  allocated_on?: string | null;
  accepted_on?: string | null;
  orchards?: Array<DroneServiceOrchards>;
  desired_date?: string | null;
  scheduled_date?: string | null;
  comments?: string;
  rate?: number;
  total_hectares?: number;
  farm_name?: string;
  client_name?: string;
  last_upload_completed_on?: string | null;
  archived_on?: string | null;
}

export interface DroneServiceOrchards {
  id: number;
  crop_type_id?: number;
  polygon?: string;
  name?: string;
  hectares?: number;
  row_spacing?: number;
  tree_spacing?: number;
}

export interface Farm {
  name: string;
  polygon: string;
  id: number;
  orchards_length?: number;
  total_hectares?: number;
  client_id: number;
  crop_family_ids?: Array<number>;
  client_name?: string;
  client_billing_status?: string;
  client_credit_card?: boolean;
  country?: string;
  province_or_state?: string;
  city?: string;
}

export interface FarmAccess {
  id?: number;
  farm_id: number;
  client_id: number;
  write_access: boolean;
  scout_access: boolean;
  client_name?: string;
  client_email?: string;
  access?: FarmAccessOption;
}

export interface FarmCropFamily {
  id?: number;
  farm_id: number;
  crop_family_id: number;
}

export interface Group {
  name: string;
  id: number;
}

export interface InternalJob {
  id?: number;
  user_id: number|null;
  survey_id: number;
  type_id: number;
  start_time: string|null;
  end_time?: string|null;
  error_time?: string|null;
  active_minutes?: number|null;
  in_progress?: boolean;
  complete?: boolean;
  has_error?: boolean;
  url?: string;
  text?: string;
  user_name?: string;
}

export interface Layer {
  id: number;
  name: string;
  symbolic: boolean;
  symbolic_color: string;
}

export interface MissingTree {
  id?: number;
  survey_id: number;
  lat: number;
  lon?: number;
  lng?: number;
}

export interface OrchardStats {
  number_trees: number|string;
  percentage_coverage: number|string;
  tree_spacing: number|string;
  row_spacing: number|string;
  survey_id: number|string;
}

export interface Orchard {
  id?: number;
  name: string;
  hectares?: number;
  surveys?: Array<Survey>;
  polygon: string;
  client_id: number;
  farm_id: number;
  crop_type_id: number;
  tree_spacing?: number;
  row_spacing?: number;
  cultivar_id?: number;
  rootstock_id?: number;
  planting_date?: string;
  trellised?: boolean;
  planting_pattern?: string;
}

export interface OrchardWithDisplayName extends Orchard {
  display_name?: string;
}

export interface OrchardDraft {
  accepted_on: string;
  client_comment: string;
  created_on: string;
  id?: number;
  orchard_id: number;
  polygon: string;
  rejected_on: string;
  staff_comment: string;
  staff_user_id: number;
}

export interface OrchardCultivar {
  id: number;
  name: string;
  crop_type_id: number;
}

export interface OrchardRootstock {
  id: number;
  name: string;
  crop_type_ids: number[];
}

export interface TreeGroup {
  id?: number;
  orchard_id: number;
  tree_ids: number[];
  name: string;
  cultivar_id: number;
  rootstock_id: number;
  plant_date: string;
  tree_group_type?: number;
}

export interface SatelliteDates {
  farm_id: number;
  dates: any;  // TODO: Add typings to this field
  excluded_dates: string;
  id: number;
  policy_id?: number;
}

export interface ServiceProvider {
  first_name: string;
  last_name: string;
  email: string;
  id?: number;
  user_id?: number;
  drone_services?: Array<DroneService>;
}

export interface Subscription {
  id?: number;
  subscription_name?: string;
  name?: string;
  price?: number;
  description?: string;
  subscription_type_id: number;
  currency: string;
  subscription_units?: any;
}

export interface SymbolicDataType {
  id: number;
  name: string;
}

export interface Survey {
  id: number;
  date: string;
  hectares: 0;
  survey_layers: Array<SurveyLayer>;
  tile_bounds: string;
  min_zoom: string;
  max_zoom: string;
  orchard_id: 0;
  upload: Upload;
  upload_id?: number;
  orchard_stats: Array<OrchardStats>;
  status_id?: number;
  s3_prefix?: string;
}

export interface SurveyFarmOverview {
  id: number;
  orchard_id: number;
  tree_count?: number;
  ndvi: number;
  ci: number;
  area: number;
  volume: number;
  height: number;
  date: string;
  missing_trees_count: number;
  health_variability: string;
  area_variability: string;
}

export interface SurveyLayer {
  layer: Layer;
  min_index: number;
  max_index: number;
}

export interface Tree {
  id: number;
  lat: number;
  lon: number;
  orchard_id: number;
  radius: number;
}

export interface TreeSurvey {
  area: number;
  height: number;
  volume: number;
  id: number;
  ndvi: number;
  ci: number;
  survey_id: number;
  tree: Tree;
  tree_id: number;
}

export interface TreeSurveyOnly {
  area: number;
  height: number;
  volume: number;
  id: number;
  ndvi: number;
  ci: number;
  ndre?: number;
  lat: number;
  lng: number;
  radius: number;
  tree_id: number;
}

export interface Upload {
  client_id: number;
  s3_prefix: string;
  client_name?: string;
  uploaded_from_client_id: number;
  sqs_message: any;
  complete?: boolean;
  processed?: boolean;
  upload_completed_on?: string;
  instance_started_on?: string;
  instance_id?: string;
  processing_completed_on?: string;
  tree_data?: boolean;
  voided?: boolean;
  processing_note?: string;
  prioritise?: boolean;
  satellite_task_id?: boolean;
  msp_data?: boolean;
  id?: number;
  tree_orchards?: Array<number>;
  drone_service_job_id?: number;
}

export interface User {
  id?: number;
  username: string;
  email: string;
  is_staff?: boolean;
  first_name: string;
  last_name: string;
  client_hierarchy?: any;
  clients?: Array<Client>;
  is_group_leader?: boolean;
  from_user_id?: number;
  group_leader?: {
    group_leader_id: number|null;
    parent_group_leader_id: number|null;
    bill_group_leader: boolean;
    bill_subscription: boolean;
    bill_drone_data: boolean;
  };
  service_provider?: ServiceProvider | null;
  groups?: Array<Group>;
  password?: string;
}

export interface UserSession {
  token: string;
  user: User;
  cognito_auth?: {
    RefreshToken: string;
    IdToken: string;
    AccessToken: string;
    TokenType: string;
    ExpiresIn: string;
  };
}

export interface Policy {
  id: number;
  name: string;
  aip_client_id: number;
  aip_client_name: string;
  aip_client_email: string;
  grower_client_id: number;
  grower_farm_ids: number[];
  shared_with_clients?: PolicySharedWithClient[];
  number_of_orchards?: number;
  total_hectares?: number;
}

export interface PolicyOrchard {
  id?: number;
  clu_id?: number;
  fsa_farm_serial_number?: number;
  fsa_farm_tract_number?: number;
  fsa_field_number?: number;
  orchard_id: number;
  policy_id: number;
  blm_sections?: any[];
  us_county: any;
  hectares?: number;
  name: string;
}

export enum PolicyAccessOption {
  Owner = 0,
  Read = 1,
  Write = 2
}

export interface AgronomyTranslation {
  related_id: number;
  category: string;
  en_US: string;
  af_za: string;
  es_ES: string;
  pt: string;
}

export interface UserSettings {
  id: number;
  client_id: number;
  new_platform: boolean;
  new_platform_home: boolean;
  measurements: 'imperial' | 'metric';
  tree_count_tour: boolean;
}
